'use client'

/* OrbitFlow command palette — opened by the nav search icon or ⌘K.
   Combines page navigation ("Jump to") with live issue/project search. */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useRouter } from 'next/navigation'
import { Search, CornerDownLeft, FolderKanban, CheckSquare, UserCircle, Loader2 } from 'lucide-react'
import { getAuthHeaders } from '@/lib/api-utils'
import { ALL_NAV_PAGES } from './nav-config'

interface SearchResult {
  id: number
  type: 'project' | 'issue' | 'user'
  title: string
  subtitle?: string
  url: string
}

interface CmdItem {
  key: string
  group: 'Jump to' | 'Results'
  label: string
  sublabel?: string
  icon: React.ReactNode
  tag?: string
  run: () => void
}

interface CommandModalProps {
  open: boolean
  onClose: () => void
}

export function CommandModal({ open, onClose }: CommandModalProps) {
  const router = useRouter()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [sel, setSel] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // Focus + reset on open
  useEffect(() => {
    if (open) {
      setQuery('')
      setResults([])
      setSel(0)
      setTimeout(() => inputRef.current?.focus(), 30)
    }
  }, [open])

  // Debounced API search
  useEffect(() => {
    if (!open) return
    const q = query.trim()
    if (q.length < 1) {
      setResults([])
      setSearching(false)
      return
    }
    setSearching(true)
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`/api/search?q=${encodeURIComponent(q)}&limit=12`, { headers: getAuthHeaders() })
        setResults(res.ok ? (await res.json()).results || [] : [])
      } catch {
        setResults([])
      } finally {
        setSearching(false)
      }
    }, 250)
    return () => clearTimeout(t)
  }, [query, open])

  const go = useCallback((url: string) => {
    router.push(url)
    onClose()
  }, [router, onClose])

  // Build the flat item list
  const items = useMemo<CmdItem[]>(() => {
    const q = query.trim().toLowerCase()
    const pages = ALL_NAV_PAGES
      .filter((p) => !q || p.name.toLowerCase().includes(q) || p.section.toLowerCase().includes(q))
      .map<CmdItem>((p) => {
        const Icon = p.icon
        return {
          key: `page-${p.href}`,
          group: 'Jump to',
          label: p.name,
          sublabel: p.section,
          icon: <Icon style={{ width: 15, height: 15 }} />,
          tag: '↵',
          run: () => go(p.href),
        }
      })
    const hits = results.map<CmdItem>((r) => ({
      key: `${r.type}-${r.id}`,
      group: 'Results',
      label: r.title,
      sublabel: r.subtitle,
      tag: r.type,
      icon: r.type === 'project'
        ? <FolderKanban style={{ width: 15, height: 15 }} />
        : r.type === 'user'
          ? <UserCircle style={{ width: 15, height: 15 }} />
          : <CheckSquare style={{ width: 15, height: 15 }} />,
      run: () => go(r.url),
    }))
    return [...pages, ...hits]
  }, [query, results, go])

  useEffect(() => { setSel(0) }, [items.length])

  // Keyboard
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose() }
      else if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, items.length - 1)) }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)) }
      else if (e.key === 'Enter') { e.preventDefault(); items[sel]?.run() }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, items, sel, onClose])

  // Keep selected item in view
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${sel}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [sel])

  if (!open) return null

  const groups: CmdItem['group'][] = ['Jump to', 'Results']
  let runningIdx = -1

  return (
    <div className="of-cmdk-backdrop" onMouseDown={onClose}>
      <div className="of-cmdk" onMouseDown={(e) => e.stopPropagation()}>
        <div className="of-cmdk-input-row">
          <Search style={{ width: 18, height: 18, color: 'var(--of-violet)', flexShrink: 0 }} />
          <input
            ref={inputRef}
            className="of-cmdk-input"
            placeholder="Search issues, projects, or jump to a page…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {searching && <Loader2 style={{ width: 16, height: 16, color: 'var(--of-fg-3)' }} className="animate-spin" />}
          <span style={{ fontFamily: 'var(--of-font-mono)', fontSize: 11, color: 'var(--of-fg-3)', border: '1px solid var(--of-border)', borderRadius: 5, padding: '1px 6px' }}>esc</span>
        </div>

        <div className="of-cmdk-list" ref={listRef}>
          {groups.map((g) => {
            const groupItems = items.filter((it) => it.group === g)
            if (groupItems.length === 0) return null
            return (
              <div key={g}>
                <div className="of-cmdk-group">{g}</div>
                {groupItems.map((it) => {
                  runningIdx += 1
                  const idx = runningIdx
                  return (
                    <button
                      key={it.key}
                      data-idx={idx}
                      className={`of-cmdk-item${idx === sel ? ' sel' : ''}`}
                      onMouseEnter={() => setSel(idx)}
                      onClick={it.run}
                    >
                      <span className="of-cmdk-item-ic">{it.icon}</span>
                      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.label}</span>
                        {it.sublabel && (
                          <span style={{ fontSize: 11.5, color: 'var(--of-fg-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {it.sublabel}
                          </span>
                        )}
                      </span>
                      {it.tag && <span className="of-cmdk-tag">{it.tag}</span>}
                    </button>
                  )
                })}
              </div>
            )
          })}
          {items.length === 0 && (
            <div style={{ padding: 32, textAlign: 'center', fontSize: 13, color: 'var(--of-fg-3)' }}>
              {query.trim() ? 'No matches found.' : 'Start typing to search.'}
            </div>
          )}
        </div>

        <div className="of-cmdk-foot">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <CornerDownLeft style={{ width: 12, height: 12 }} /> to select
          </span>
          <span>↑↓ to navigate</span>
          <span style={{ marginLeft: 'auto' }}>OrbitFlow search</span>
        </div>
      </div>
    </div>
  )
}
